import { createContext, useCallback, useContext, useRef, useState } from 'react'

/**
 * Default expand phase when the nav is not expanding.
 * @typedef {Object} ExpandPhase
 * @property {boolean} isExpanding - True while the nav is transitioning from minimized to default.
 * @property {boolean} expandFadeOutDone - True after the initial fade-out (narrow bar) phase.
 * @property {boolean} expandFadeInStarted - True when the fade-in phase has started (e.g. at 350ms).
 * @property {boolean} expandToggleStyleApplied - True when the toggle button may show expanded style (after 200ms delay).
 */
const defaultExpandPhase = {
  isExpanding: false,
  expandFadeOutDone: true,
  expandFadeInStarted: true,
  expandToggleStyleApplied: true
}

/**
 * Default collapse phase when the nav is not collapsing.
 * @typedef {Object} CollapsePhase
 * @property {boolean} isCollapsing - True during the collapse sequence (fade-out, width, toggle style).
 * @property {boolean} collapseWidthApplied - True after the fade-out phase when the bar width starts shrinking.
 * @property {boolean} collapseWidthAppliedForCreateButton - True when the Create button may show minimized style.
 * @property {boolean} collapseToggleStyleApplied - True when the toggle button may show collapsed (circular) style.
 */
const defaultCollapsePhase = {
  isCollapsing: false,
  collapseWidthApplied: false,
  collapseWidthAppliedForCreateButton: false,
  collapseToggleStyleApplied: false
}

const Context = createContext({
  setPortalRendered: () => {},
  setCustomSidenav: () => {},
  ...defaultExpandPhase,
  ...defaultCollapsePhase,
  setExpandPhase: () => {},
  setCollapsePhase: () => {}
})

/**
 * Expand phase (fade out/in) provided by LeftNavigation so nav items see it on first paint.
 * Used in addition to the main context to avoid a one-frame delay when expanding.
 */
const ExpandPhaseContext = createContext(defaultExpandPhase)
export const ExpandPhaseProvider = ExpandPhaseContext.Provider

/**
 * Returns the current expand phase (isExpanding, expandFadeOutDone, expandFadeInStarted, expandToggleStyleApplied).
 * Use inside ExpandPhaseProvider (e.g. within LeftNavigation) for first-paint accuracy.
 * @returns {ExpandPhase}
 */
export const useExpandPhaseContext = () => useContext(ExpandPhaseContext)

const { Provider } = Context

/**
 * Provider for left navigation shared state: portal ref, expand/collapse phase, and setters.
 * Expand and collapse phases are updated via setExpandPhase/setCollapsePhase so that nav items
 * and the toggle button can react to the same timing (fade-out, width, fade-in, toggle style).
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {React.ReactElement}
 */
export const LeftNavigationContext = ({ children }) => {
  const portalRef = useRef()
  const [portalRendered, setPortalRendered] = useState(false)
  const [isCustomSideNav, setCustomSidenav] = useState(false)
  const [refBound, setRefBound] = useState(false)
  const expandPhaseRef = useRef(defaultExpandPhase)
  const collapsePhaseRef = useRef(defaultCollapsePhase)
  const [, setExpandVersion] = useState(0)
  const [, setCollapseVersion] = useState(0)

  const callbackRef = useCallback((ref) => {
    portalRef.current = ref
    setRefBound(!!ref)
  }, [])

  /**
   * Merges updates into the expand phase ref and triggers a re-render so consumers see the new phase.
   * @param {Partial<ExpandPhase>} updates
   */
  const setExpandPhase = useCallback((updates) => {
    expandPhaseRef.current = { ...expandPhaseRef.current, ...updates }
    setExpandVersion((v) => v + 1)
  }, [])

  /**
   * Merges updates into the collapse phase ref and triggers a re-render so consumers see the new phase.
   * @param {Partial<CollapsePhase>} updates
   */
  const setCollapsePhase = useCallback((updates) => {
    collapsePhaseRef.current = { ...collapsePhaseRef.current, ...updates }
    setCollapseVersion((v) => v + 1)
  }, [])

  return (
    <Provider value={{
      bindRef: callbackRef,
      portalRef,
      portalRendered,
      setPortalRendered,
      isCustomSideNav,
      setCustomSidenav,
      refBound,
      ...expandPhaseRef.current,
      ...collapsePhaseRef.current,
      setExpandPhase,
      setCollapsePhase
    }}
    >
      {children}
    </Provider>
  )
}

/**
 * Returns the left navigation context value: portal ref, portal/custom-nav state, expand phase,
 * collapse phase, and setExpandPhase/setCollapsePhase. Use for reading or driving expand/collapse timing.
 *
 * @returns {{
 *   bindRef: (ref: React.RefObject | null) => void,
 *   portalRef: React.RefObject,
 *   portalRendered: boolean,
 *   setPortalRendered: (value: boolean) => void,
 *   isCustomSideNav: boolean,
 *   setCustomSidenav: (value: boolean) => void,
 *   refBound: boolean,
 *   isExpanding: boolean,
 *   expandFadeOutDone: boolean,
 *   expandFadeInStarted: boolean,
 *   expandToggleStyleApplied: boolean,
 *   isCollapsing: boolean,
 *   collapseWidthApplied: boolean,
 *   collapseWidthAppliedForCreateButton: boolean,
 *   collapseToggleStyleApplied: boolean,
 *   setExpandPhase: (updates: Partial<ExpandPhase>) => void,
 *   setCollapsePhase: (updates: Partial<CollapsePhase>) => void
 * }}
 */
export const useLeftNavigationContext = () => useContext(Context)
